import { type FC, useState, useEffect } from 'react';
import { useFragment, graphql, useRelayEnvironment } from 'react-relay';
import { useIsSellerOnline } from 'dibs-presence/exports/useIsSellerOnline.ts';
import UserCircle from 'dibs-icons/exports/UserCircle.ts';
import Mask from 'dibs-icons/exports/legacy/Mask.ts';
import ArrowUp from 'dibs-icons/exports/legacy/ArrowUp.ts';
import { Tooltip } from 'dibs-elements/exports/Tooltip.tsx';
import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import Circle from 'dibs-icons/exports/legacy/Circle.ts';
import { trackNavInteraction } from '../../helpers/tracking.ts';
import { userServerVars } from '../../userServerVars.ts';
import { useNavContext } from '../../NavContext.tsx';
import { eventNameConstants, interactionTypeConstants } from 'dibs-tracking';
import { isMobile } from 'dibs-media-query';

import classnames from 'classnames';
import dibsCss from 'dibs-css';
import styles from './styles/NavProfile.scss';

import { type NavProfile_viewer$key } from './__generated__/NavProfile_viewer.graphql.ts';
import { type NavProfile_seller$key } from './__generated__/NavProfile_seller.graphql.ts';

type Props = {
    viewer?: NavProfile_viewer$key | null;
    seller?: NavProfile_seller$key | null;
    showBorder?: boolean;
    hideArrow?: boolean;
};

const viewerFragment = graphql`
    fragment NavProfile_viewer on Viewer {
        presenceFeatureFlag: featureFlag(feature: "presence")
        ...useIsSellerOnline_viewer
    }
`;

const sellerFragment = graphql`
    fragment NavProfile_seller on Seller {
        ...useIsSellerOnline_seller
        sellerProfile {
            company
        }
    }
`;

export const NavProfile: FC<Props> = ({
    viewer: viewerRef,
    seller: sellerRef,
    showBorder = true,
    hideArrow = false,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const seller = useFragment(sellerFragment, sellerRef);
    const environment = useRelayEnvironment();
    const [isMenuVisible, setIsMenuVisible] = useState(false);
    const { isDealer, masqueradeSeller, user } = userServerVars;
    const { status } = useNavContext();
    const isPresenceEnabled = !!viewer?.presenceFeatureFlag;
    const sellerCompany = seller?.sellerProfile?.company;

    useEffect(() => {
        if (isMenuVisible) {
            trackNavInteraction({
                action: `${eventNameConstants.EVENT_USERNAME} dropdown`,
                label: eventNameConstants.EVENT_USERNAME,
                interactionType: interactionTypeConstants.INTERACTION_TYPE_DROP_DOWN,
                isImpression: true,
            });
        }
    }, [isMenuVisible]);

    useEffect(() => {
        if (status === 'collapsed') {
            setIsMenuVisible(false);
        }
    }, [status]);

    const isSellerOnline = useIsSellerOnline({
        environment,
        viewer,
        seller,
    });

    if (isMobile() && status === 'collapsed') {
        return null;
    }

    const userOrSellerName = isDealer ? sellerCompany : user?.profile?.displayName;

    return (
        <div
            onClick={() => {
                trackNavInteraction({
                    action: eventNameConstants.EVENT_USERNAME,
                    label: eventNameConstants.EVENT_USERNAME,
                    interactionType: interactionTypeConstants.INTERACTION_TYPE_GLOBAL_NAV,
                });
                setIsMenuVisible(prev => !prev);
            }}
            className={classnames(
                styles.profileContainer,
                dibsCss.mtAuto,
                dibsCss.border0,
                {
                    [dibsCss.borderT]: showBorder,
                    [dibsCss.borderSolid]: showBorder,
                    [dibsCss.borderDolphin]: showBorder,
                },
                dibsCss.cursorPointer
            )}
        >
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.itemsCenter,
                    dibsCss.justifyCenter,
                    dibsCss.relative,
                    dibsCss.hFull,
                    dibsCss.pxSmall,
                    { [dibsCss.gapXsmall]: status === 'expanded' || status === 'overlay' }
                )}
                data-tn="seller-profile"
            >
                <div
                    className={classnames({
                        [dibsCss.fillNoir]: isDealer,
                        [dibsCss.fillWhite]: !isDealer,
                    })}
                >
                    {masqueradeSeller ? (
                        <div className={classnames(dibsCss.w20px, dibsCss.h20px)}>
                            <Mask />
                        </div>
                    ) : (
                        <div className={dibsCss.relative}>
                            {isDealer && isPresenceEnabled && (
                                <Circle
                                    className={classnames(
                                        styles.circleIcon,
                                        dibsCss.absolute,
                                        dibsCss.bottom0,
                                        {
                                            [dibsCss.fillSuccessprimary]: isSellerOnline,
                                            [dibsCss.fillIron]: !isSellerOnline,
                                        }
                                    )}
                                />
                            )}
                            <UserCircle variation="20" />
                        </div>
                    )}
                </div>
                <div
                    className={classnames(
                        styles.profile,
                        dibsCss.flex,
                        dibsCss.gapXsmall,
                        dibsCss.itemsCenter,
                        dibsCss.overflowHidden,
                        {
                            [dibsCss.opacity0]: status === 'collapsed',
                            [dibsCss.opacity100]: status === 'expanded' || status === 'overlay',
                        }
                    )}
                >
                    {userOrSellerName && (
                        <div
                            className={classnames(
                                dibsCss.sassyFontBodySmallHeavy,
                                dibsCss.overflowHidden,
                                dibsCss.whitespaceNowrap,
                                dibsCss.textEllipsis,
                                {
                                    [dibsCss.textNoir]: isDealer,
                                    [dibsCss.textWhite]: !isDealer,
                                }
                            )}
                            data-tn="seller-company"
                        >
                            {userOrSellerName}
                        </div>
                    )}
                    {!hideArrow && (
                        <div className={classnames(dibsCss.h14px, dibsCss.w14px, dibsCss.shrink0)}>
                            <ArrowUp
                                className={classnames({
                                    [dibsCss.fillNoir]: isDealer,
                                    [dibsCss.fillWhite]: !isDealer,
                                })}
                            />
                        </div>
                    )}
                </div>
                <Tooltip
                    isVisible={isMenuVisible}
                    type="autoWidth"
                    direction="top"
                    paddingSize="none"
                    closeOnOutsideClick
                    onClose={() => setIsMenuVisible(false)}
                >
                    <div
                        className={classnames(dibsCss.pSmall, {
                            [styles.tooltip]: status === 'expanded' || status === 'overlay',
                        })}
                    >
                        <Link
                            href={`/logout/${isDealer ? 'dealer' : 'internal'}`}
                            dataTn="logout-link"
                            onClick={() =>
                                trackNavInteraction({
                                    action: 'logout',
                                    label: 'logout',
                                    interactionType: 'drop down',
                                })
                            }
                        >
                            <FormattedMessage
                                id="dal.header.userMenuOptions.logout"
                                defaultMessage="Logout"
                            />
                        </Link>
                    </div>
                </Tooltip>
            </div>
        </div>
    );
};
